import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { fadeUp } from "@/lib/motion";
import { useSeo } from "@/hooks/useSeo";
import { SERVICES, IMAGES } from "@/data/content";

export default function Services() {
  useSeo(
    "Services | Automotive Certification & Homologation | AutoDrive Tech Solutions",
    "Type Approval Certification, Conformity of Production, Regulatory Consulting, Testing Management, Documentation & Submission and Component & System Certification."
  );

  return (
    <>
      <PageHero
        overline="Our Services"
        title="End-to-End Certification Services"
        subtitle="Six specialized services covering the complete automotive certification lifecycle in India."
        image={IMAGES.services}
        crumb="Services"
      />

      {/* Service index */}
      <section className="py-16 bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 flex flex-wrap gap-3">
          {SERVICES.map((s) => (
            <a
              key={s.slug}
              href={`#${s.slug}`}
              data-testid={`service-jump-${s.slug}`}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-[#1E40AF] hover:text-[#1E40AF] transition-colors"
            >
              <s.icon className="w-4 h-4" strokeWidth={1.7} />
              {s.title.replace(/\s*\(.*\)/, "")}
            </a>
          ))}
        </div>
      </section>

      {SERVICES.map((s, idx) => (
        <section
          key={s.slug}
          id={s.slug}
          className={`scroll-mt-24 py-24 sm:py-28 ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}
          data-testid={`service-detail-${s.slug}`}
        >
          <div className="max-w-[1320px] mx-auto px-5 sm:px-8">
            <div className={`grid lg:grid-cols-12 gap-12 lg:gap-16 items-start`}>
              {/* Left: intro */}
              <Reveal className="lg:col-span-5">
                <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#1E40AF] text-white shadow-lg shadow-blue-900/20">
                  <s.icon className="w-7 h-7" strokeWidth={1.5} />
                </span>
                <span className="mt-6 block text-xs font-semibold tracking-[0.25em] uppercase text-[#1E40AF]">
                  Service {String(idx + 1).padStart(2, "0")}
                </span>
                <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl text-[#0F172A] leading-tight tracking-tight">
                  {s.title}
                </h2>
                <p className="mt-5 text-slate-600 leading-relaxed">{s.description}</p>
                <Link
                  to="/book-consultation"
                  data-testid={`service-cta-${s.slug}`}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1E40AF] text-white font-semibold px-6 py-3 transition-colors"
                >
                  Discuss This Service <ArrowRight className="w-4 h-4" />
                </Link>
              </Reveal>

              {/* Right: benefits + process */}
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
                <Reveal variants={fadeUp} className="rounded-2xl border border-slate-200 bg-white p-7">
                  <h3 className="font-display font-semibold text-lg text-[#0F172A]">Key Benefits</h3>
                  <ul className="mt-5 space-y-3.5">
                    {s.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-slate-600">
                        <CheckCircle2 className="w-5 h-5 text-[#16A34A] shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
                <Reveal variants={fadeUp} custom={1} className="rounded-2xl border border-slate-200 bg-[#0F172A] p-7">
                  <h3 className="font-display font-semibold text-lg text-white">Our Process</h3>
                  <ol className="mt-5 space-y-4">
                    {s.process.map((p, i) => (
                      <li key={p} className="flex items-center gap-4">
                        <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#1E40AF] text-white text-sm font-bold shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-sm text-slate-300">{p}</span>
                      </li>
                    ))}
                  </ol>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ))}

      <CTASection />
    </>
  );
}
